/**
 * Web-Bluetooth driver for GOOJPRT-style 58mm thermal printers.
 *
 * GOOJPRT (PT-210 / PT-280 / MTP-3 / MTP-2 etc.) and most cheap chinese
 * thermal printers expose a single BLE service with a write characteristic.
 * Three UUID conventions cover the entire family:
 *
 *   1) Generic printer service          18F0 / 2AF1
 *   2) HM-10 style serial bridge        FFE0 / FFE1
 *   3) Nordic UART (some clones)        6E400001-... / 6E400002-...
 *
 * The driver auto-detects which one the connected device exposes.
 *
 * Why Web Bluetooth and not OS-level print? GOOJPRT do not implement the
 * AirPrint/IPP protocol — they only talk raw ESC/POS over a BLE link, so
 * the browser print dialog can't see them at all. Web Bluetooth (Chrome,
 * Edge, Opera, Samsung Internet) lets us pair and write directly without
 * installing the manufacturer's app.
 *
 * Browser support: Chrome/Edge on Android, macOS, Windows, Chromebook.
 * Not on iOS Safari (use the fallback HTML printer there).
 */

const CANDIDATES = [
    { service: 0x18f0, write: 0x2af1 },
    { service: 0xffe0, write: 0xffe1 },
    {
        service: "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
        write:   "6e400002-b5a3-f393-e0a9-e50e24dcca9e",
    },
    { service: 0xff00, write: 0xff02 },
];

const ALL_SERVICES = CANDIDATES.map((c) => c.service);

// ---- Module-level cache so a single pairing covers an entire session.
let cachedDevice = null;
let cachedChar = null;
let cachedServer = null;
let listeners = new Set();

const STORAGE_KEY = "jw_bt_printer_name";
const PROFILES_KEY = "jw_printer_profiles";
const ROLES_KEY = "jw_printer_roles";

// -------------------------------------------- PROFILES (persisted in localStorage)
/**
 * Each profile describes ONE physical printer. A profile is a pure JSON
 * config (not a BLE handle) so it survives page reloads. The user picks
 * which profile is "active" at runtime; when they hit Print we pair the
 * device and apply that profile's quirks.
 */
const DEFAULT_PROFILE = {
    id: "default",
    name: "Impresora térmica 58mm",
    width: 58,             // 58 or 80 mm
    chars: 32,             // line width in characters
    codepage: 0,           // ESC/POS codepage: 0=CP437, 16=WPC1252, 19=PC858
    cutMode: "partial",    // partial | none
    beepOnPrint: false,    // sound a beep before printing
    density: 0,            // -3 to +3, applied via GS ( E
    feedAfter: 4,          // blank lines to feed at the end
    invertAccents: false,  // some clones need transliteration
};

export function loadProfiles() {
    try {
        const raw = localStorage.getItem(PROFILES_KEY);
        if (!raw) return [DEFAULT_PROFILE];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) && arr.length ? arr : [DEFAULT_PROFILE];
    } catch {
        return [DEFAULT_PROFILE];
    }
}

export function saveProfiles(profiles) {
    localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
    emit();
}

export function loadRoles() {
    try {
        const raw = localStorage.getItem(ROLES_KEY);
        return raw ? JSON.parse(raw) : { customer: "default", kitchen: "default" };
    } catch {
        return { customer: "default", kitchen: "default" };
    }
}

export function saveRoles(roles) {
    localStorage.setItem(ROLES_KEY, JSON.stringify(roles));
    emit();
}

export function getProfile(id) {
    return loadProfiles().find((p) => p.id === id) || loadProfiles()[0];
}

export function getProfileForRole(role) {
    const roles = loadRoles();
    return getProfile(roles[role] || "default");
}

function emit() {
    const state = getState();
    listeners.forEach((cb) => {
        try { cb(state); } catch { /* noop */ }
    });
}

export function subscribePrinterState(cb) {
    listeners.add(cb);
    cb(getState());
    return () => listeners.delete(cb);
}

export function getState() {
    return {
        connected: !!(cachedDevice && cachedDevice.gatt && cachedDevice.gatt.connected),
        name: cachedDevice?.name || localStorage.getItem(STORAGE_KEY) || "",
        supported: typeof navigator !== "undefined" && !!navigator.bluetooth,
    };
}

async function findWritableChar(server) {
    for (const c of CANDIDATES) {
        try {
            const svc = await server.getPrimaryService(c.service);
            const ch = await svc.getCharacteristic(c.write);
            if (ch.properties.write || ch.properties.writeWithoutResponse) {
                return ch;
            }
        } catch {
            // try next
        }
    }
    // Last resort: walk every service to find a write characteristic.
    const services = await server.getPrimaryServices();
    for (const svc of services) {
        const chars = await svc.getCharacteristics();
        for (const ch of chars) {
            if (ch.properties.write || ch.properties.writeWithoutResponse) {
                return ch;
            }
        }
    }
    throw new Error("No writable characteristic found on this printer");
}

export async function pairPrinter() {
    if (!navigator.bluetooth) {
        throw new Error("Tu navegador no soporta Web Bluetooth. Usa Chrome o Edge en Android/PC.");
    }
    // acceptAllDevices=true so we don't filter out clones with unknown service
    // UUIDs in their advertisement; we still scan for our known services
    // afterwards. `optionalServices` is required to actually access them later.
    const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ALL_SERVICES,
    });
    device.addEventListener("gattserverdisconnected", onDisconnected);

    const server = await device.gatt.connect();
    const ch = await findWritableChar(server);

    cachedDevice = device;
    cachedServer = server;
    cachedChar = ch;
    if (device.name) localStorage.setItem(STORAGE_KEY, device.name);
    emit();
    return { name: device.name || "Bluetooth printer" };
}

function onDisconnected() {
    cachedServer = null;
    cachedChar = null;
    emit();
}

async function ensureConnected() {
    if (cachedChar && cachedDevice?.gatt?.connected) return cachedChar;
    if (cachedDevice && !cachedDevice.gatt.connected) {
        const server = await cachedDevice.gatt.connect();
        cachedServer = server;
        cachedChar = await findWritableChar(server);
        emit();
        return cachedChar;
    }
    // Need a fresh pairing — this needs a user gesture, so we surface the
    // requirement back to the caller.
    throw new Error("NO_PRINTER");
}

export async function disconnectPrinter() {
    if (cachedDevice?.gatt?.connected) {
        try { cachedDevice.gatt.disconnect(); } catch { /* noop */ }
    }
    cachedDevice = null;
    cachedServer = null;
    cachedChar = null;
    localStorage.removeItem(STORAGE_KEY);
    emit();
}

async function writeBytes(bytes) {
    const ch = await ensureConnected();
    // BLE MTU is usually 20–185. Chunk to 180 to be safe across stacks.
    const CHUNK = 180;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        const slice = bytes.slice(i, i + CHUNK);
        if (ch.properties.writeWithoutResponse) {
            await ch.writeValueWithoutResponse(slice);
        } else {
            await ch.writeValue(slice);
        }
        // Small pacing keeps the print head buffer happy.
        await new Promise((r) => setTimeout(r, 18));
    }
}

// ---------------------------------------------------------- ESC/POS builder
const enc = new TextEncoder();
function append(arr, ...parts) {
    for (const p of parts) {
        if (typeof p === "string") arr.push(...enc.encode(p));
        else if (Array.isArray(p)) arr.push(...p);
        else if (p instanceof Uint8Array) arr.push(...p);
        else arr.push(p);
    }
}

// Common commands
const INIT       = [0x1b, 0x40];
const FEED       = [0x0a];
const ALIGN_LEFT   = [0x1b, 0x61, 0x00];
const ALIGN_CENTER = [0x1b, 0x61, 0x01];
const BOLD_ON    = [0x1b, 0x45, 0x01];
const BOLD_OFF   = [0x1b, 0x45, 0x00];
const SIZE_2X    = [0x1d, 0x21, 0x11];
const SIZE_NORMAL = [0x1d, 0x21, 0x00];
const CUT        = [0x1d, 0x56, 0x00];   // partial cut where supported
const BEEP       = [0x1b, 0x42, 0x03, 0x02];

function CODEPAGE(n) { return [0x1b, 0x74, Number(n) & 0xff]; }
// Print density: GS ( E pL pH fn [-3..+3]
function DENSITY(level) {
    const v = Math.max(-3, Math.min(3, Number(level) || 0));
    return [0x1d, 0x28, 0x45, 0x03, 0x00, 0x05, 0x05, v & 0xff];
}

function repeat(ch, n) { return ch.repeat(Math.max(0, n)); }
function pad(left, right, width) {
    left = String(left ?? "");
    right = String(right ?? "");
    const space = Math.max(1, width - left.length - right.length);
    return left + repeat(" ", space) + right;
}

// Crude ASCII fold (GOOJPRT default code page is CP437; Spanish accents
// need transliteration to come out clean).
const REPLACEMENTS = {
    "á":"a","é":"e","í":"i","ó":"o","ú":"u",
    "Á":"A","É":"E","Í":"I","Ó":"O","Ú":"U",
    "ñ":"n","Ñ":"N","ü":"u","Ü":"U","¿":"?","¡":"!",
    "—":"-","–":"-","“":"\"","”":"\"","‘":"'","’":"'",
    "·":"-","…":"...",
};
function fold(s = "") {
    return String(s).replace(/[áéíóúÁÉÍÓÚñÑüÜ¿¡—–“”‘’·…]/g, (c) => REPLACEMENTS[c] || c);
}

function wrap(text, width) {
    const out = [];
    let line = "";
    for (const word of fold(text).split(/\s+/)) {
        if (!word) continue;
        if ((line + " " + word).trim().length > width) {
            if (line) out.push(line);
            line = word;
        } else {
            line = (line ? line + " " : "") + word;
        }
    }
    if (line) out.push(line);
    return out.length ? out : [""];
}

function moneyStr(n) { return `$${(Number(n) || 0).toFixed(2)}`; }

function buildReceiptBytes(order, opts = {}) {
    const {
        kind = "customer",
        businessName = "J & W Deli",
        businessLine = "New Haven, CT",
        footer = "Gracias por su compra",
        profile = DEFAULT_PROFILE,
    } = opts;
    const cfg = { ...DEFAULT_PROFILE, ...profile };
    const W = Math.max(20, Math.min(64, Number(cfg.chars) || 32));
    const isKitchen = kind === "kitchen";

    const out = [];
    append(out, INIT);
    // Apply codepage + density tweaks from the profile.
    append(out, CODEPAGE(cfg.codepage || 0));
    if (cfg.density) append(out, DENSITY(cfg.density));

    // ----- header
    append(out, ALIGN_CENTER, SIZE_2X, BOLD_ON, fold(businessName), FEED, BOLD_OFF, SIZE_NORMAL);
    append(out, fold(businessLine), FEED);
    if (isKitchen) {
        append(out, BOLD_ON);
        append(out, fold((order.fulfillment || "PICKUP").toUpperCase() +
            (order.channel === "pos" ? " - POS" : "")), FEED);
        append(out, BOLD_OFF);
    } else {
        const ts = order.created_at ? new Date(order.created_at).toLocaleString() : "";
        append(out, fold(ts), FEED);
    }
    append(out, ALIGN_LEFT);
    append(out, repeat("-", W), FEED);

    // ----- meta
    const idShort = (order.id || "").slice(-8).toUpperCase();
    append(out, pad("Orden", "#" + idShort, W), FEED);
    if (order.customer_name) {
        append(out, pad("Cliente", order.customer_name.slice(0, Math.max(8, W - 10)), W), FEED);
    }
    if (order.customer_phone) {
        append(out, pad("Tel", order.customer_phone, W), FEED);
    }
    if (!isKitchen) {
        append(out, pad("Tipo", (order.fulfillment || "pickup").toUpperCase(), W), FEED);
    }
    append(out, repeat("-", W), FEED);

    // ----- items
    for (const it of (order.items || [])) {
        const qty = String(it.qty || 1);
        const name = it.name || "";
        const amount = isKitchen ? "" : moneyStr(it.line_total ?? (it.price || 0) * (it.qty || 1));
        if (isKitchen) {
            append(out, SIZE_2X, BOLD_ON, fold(qty + "x"), FEED, BOLD_OFF, SIZE_NORMAL);
            for (const ln of wrap(name, W)) {
                append(out, "  " + ln, FEED);
            }
        } else {
            const lines = wrap(name, W - 8);
            const head = `${qty}x ${lines[0]}`;
            append(out, pad(head, amount, W), FEED);
            for (let i = 1; i < lines.length; i++) {
                append(out, "   " + lines[i], FEED);
            }
        }
    }
    append(out, repeat("-", W), FEED);

    // ----- notes
    if (order.notes) {
        append(out, BOLD_ON);
        const tag = isKitchen ? "*** NOTA ***" : "Nota cliente";
        append(out, fold(tag), FEED);
        for (const ln of wrap(order.notes, W)) {
            append(out, fold(ln), FEED);
        }
        append(out, BOLD_OFF);
        append(out, repeat("-", W), FEED);
    }

    // ----- totals (customer only)
    if (!isKitchen) {
        append(out, pad("Subtotal", moneyStr(order.subtotal), W), FEED);
        if (order.delivery_fee) append(out, pad("Delivery", moneyStr(order.delivery_fee), W), FEED);
        if (order.discount) append(out, pad("Descuento", "-" + moneyStr(order.discount), W), FEED);
        append(out, pad(`Impuesto ${((order.tax_rate || 0.0735) * 100).toFixed(2)}%`, moneyStr(order.tax), W), FEED);
        if (order.tip) append(out, pad("Propina", moneyStr(order.tip), W), FEED);
        append(out, SIZE_2X, BOLD_ON, pad("TOTAL", moneyStr(order.total), Math.floor(W / 2)), FEED, BOLD_OFF, SIZE_NORMAL);
        if (order.payment_method === "cash") {
            append(out, pad("Efectivo", moneyStr(order.cash_received), W), FEED);
            append(out, pad("Cambio", moneyStr(order.change_due), W), FEED);
        } else {
            append(out, pad("Pago", (order.payment_method || "").toUpperCase(), W), FEED);
        }
        append(out, repeat("-", W), FEED);
        append(out, ALIGN_CENTER, fold(footer), FEED, "jwdeli.com", FEED);
        if (order.id) {
            append(out, FEED, BOLD_ON, "Sigue tu pedido:", FEED, BOLD_OFF);
            append(out, fold(`jwdeli.com/order/${order.id}`), FEED);
        }
        append(out, ALIGN_LEFT);
    } else {
        append(out, ALIGN_CENTER);
        const ts = order.created_at ? new Date(order.created_at).toLocaleString() : "";
        append(out, fold(ts), FEED, ALIGN_LEFT);
    }

    // Spacer + beep + (optional) cut
    const blankLines = Math.max(0, Math.min(10, Number(cfg.feedAfter) || 0));
    for (let i = 0; i < blankLines; i++) append(out, FEED);
    if (cfg.beepOnPrint && isKitchen) append(out, BEEP);
    if (cfg.cutMode !== "none") append(out, CUT);

    return new Uint8Array(out);
}

/** Print an order receipt via the currently paired BT printer. */
export async function printOrder(order, opts = {}) {
    // Auto-resolve profile from role if not provided.
    const profile = opts.profile || (opts.kind === "kitchen"
        ? getProfileForRole("kitchen")
        : getProfileForRole("customer"));
    const bytes = buildReceiptBytes(order, { ...opts, profile });
    await writeBytes(bytes);
}

/** Print a "Hello / connection OK" test page so the cashier can verify pairing. */
export async function printTestPage(profile) {
    const cfg = { ...DEFAULT_PROFILE, ...(profile || getProfile("default")) };
    const W = Math.max(20, Math.min(64, Number(cfg.chars) || 32));
    const out = [];
    append(out, INIT, CODEPAGE(cfg.codepage || 0));
    if (cfg.density) append(out, DENSITY(cfg.density));
    append(out, ALIGN_CENTER, SIZE_2X, BOLD_ON, "J & W DELI", FEED, BOLD_OFF, SIZE_NORMAL);
    append(out, "Bluetooth ok", FEED, repeat("-", W), FEED);
    append(out, ALIGN_LEFT, fold(`Perfil: ${cfg.name}`), FEED);
    append(out, fold(`Ancho: ${cfg.width}mm / ${W} chars`), FEED);
    append(out, fold(`Codepage: ${cfg.codepage} · Corte: ${cfg.cutMode}`), FEED, FEED);
    append(out, "Caracteres especiales:", FEED);
    append(out, "Mofongo de Camarones - Pina", FEED);
    append(out, "Limonada - Cafe con Leche", FEED, FEED);
    append(out, ALIGN_CENTER);
    if (cfg.beepOnPrint) append(out, BEEP);
    for (let i = 0; i < (cfg.feedAfter || 4); i++) append(out, FEED);
    if (cfg.cutMode !== "none") append(out, CUT);
    await writeBytes(new Uint8Array(out));
}
